import { PrestationPricingMode, PrestationType } from '@cpce/database/enums';
import {
  AdminDeletionTarget,
  type FaceValueCriteria,
  getPrestationKind,
  isSubsidisedByBilletterie,
  type PrestationKind,
} from '@cpce/shared';
import { getRouteApi } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { createColumnHelper } from '@tanstack/react-table';
import Decimal from 'decimal.js';
import {
  Archive,
  ArchiveRestore,
  Check,
  CircleQuestionMark,
  Copy,
  Pencil,
  Plus,
  SlidersHorizontal,
  Trash,
  X,
} from 'lucide-react';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import {
  useCreatePrestationPrice,
  useDeletePrestationPrice,
  useSetPrestationArchived,
  useUpdatePrestation,
  useUpdatePrestationPrice,
} from '@/api/prestations/prestations.mutations';
import { usePrestation } from '@/api/prestations/prestations.queries';
import { isNotFoundError } from '@/api/queries.utils';
import { AdminDeleteButton } from '@/components/AdminDeleteButton';
import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import DecimalInput from '@/components/DecimalInput';
import { Input } from '@/components/Input';
import { ModalButton } from '@/components/ModalButton';
import { NotFoundState } from '@/components/NotFoundState';
import { StickyHeaderPage } from '@/components/StickyHeaderPage';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/Tabs';
import { Tooltip } from '@/components/Tooltip';
import { Table } from '@/components/table/Table';
import { useTableState } from '@/components/table/useTableState';
import { useCommiteeSlugNavigate } from '@/utils/router';
import { formatDecimalAsPrice } from '@/utils/string.utils';
import { describeFaceValueCriteria, FaceValueCriteriaModal } from './FaceValueCriteriaModal';
import { PrestationInformation } from './PrestationInformation';
import { PrestationJustificatifSetting } from './PrestationJustificatifSetting';
import { PrestationQuestions } from './PrestationQuestions';
import { PrestationSubsidyRules } from './PrestationSubsidyRules';
import { PrestationTypeBadge } from './PrestationTypeBadge';

const routeApi = getRouteApi('/$commiteeSlug/prestation/{-$id}');

export enum PrestationTabs {
  Informations = 'informations',
  Tarifs = 'tarifs',
}

export function PrestationPage() {
  const { id } = routeApi.useParams();
  const { tab = PrestationTabs.Informations } = routeApi.useSearch();
  const routeNavigate = routeApi.useNavigate();
  const prestationId = Number(id);
  const { data: prestation, error } = usePrestation(prestationId);
  const updatePrestation = useUpdatePrestation(prestationId);
  const setArchived = useSetPrestationArchived(prestationId);
  const navigate = useCommiteeSlugNavigate();

  const breadcrumbItems = [
    { label: 'Prestations', navigate: { to: '/$commiteeSlug/prestation' as const } },
    { label: prestation?.name ?? 'Chargement...' },
  ];

  if (!prestation && isNotFoundError(error)) {
    return (
      <NotFoundState
        title="Prestation introuvable"
        backTo="/$commiteeSlug/prestation"
        backLabel="Retour aux prestations"
      />
    );
  }

  if (!prestation) {
    return (
      <StickyHeaderPage pathLink={breadcrumbItems} title="Chargement...">
        <div className="flex items-center justify-center py-12">
          <div className="text-gray-500">Chargement de la prestation...</div>
        </div>
      </StickyHeaderPage>
    );
  }

  const isArchived = prestation.archivedAt !== null;

  return (
    <Tabs
      value={tab}
      onValueChange={(value) =>
        routeNavigate({
          search: (prev) => ({ ...prev, tab: value as PrestationTabs }),
          replace: true,
        })
      }
    >
      <StickyHeaderPage
        pathLink={breadcrumbItems}
        title={prestation.name}
        badges={
          <>
            <PrestationTypeBadge type={prestation.type} />
            {isArchived && <Badge>Archivée</Badge>}
          </>
        }
        topRightActions={
          <>
            <Button
              variant="outline"
              icon={Copy}
              onClick={() =>
                navigate({
                  to: '/$commiteeSlug/prestation/create',
                  search: { duplicateFrom: prestationId },
                })
              }
            >
              Dupliquer
            </Button>
            <Button
              variant="outline"
              icon={isArchived ? ArchiveRestore : Archive}
              onClick={() => setArchived.mutate(!isArchived)}
              isPending={setArchived.isPending}
            >
              {isArchived ? 'Désarchiver' : 'Archiver'}
            </Button>
            <AdminDeleteButton
              target={AdminDeletionTarget.prestation}
              id={prestationId}
              onDeleted={() => navigate({ to: '/$commiteeSlug/prestation' })}
            />
          </>
        }
        subHeader={
          <TabsList>
            <TabsTrigger value="informations">Informations</TabsTrigger>
            <TabsTrigger value="tarifs">Tarifs</TabsTrigger>
          </TabsList>
        }
      >
        <TabsContent value="informations" className="flex flex-col gap-6">
          <PrestationInformation
            prestation={prestation}
            onSave={(data) => updatePrestation.mutate(data)}
          />
        </TabsContent>
        <TabsContent value="tarifs" className="flex flex-col gap-6">
          {prestation.pricingMode === PrestationPricingMode.typedAmount ? (
            <div className="flex flex-col gap-1">
              <h3 className="text-lg font-medium">Tarifs individuels</h3>
              <p className="text-sm text-gray-500">
                Le montant de chaque participant est saisi à l'inscription : cette prestation n'a
                pas de tarif.
              </p>
            </div>
          ) : (
            <PrestationTarifs
              prestationId={prestationId}
              prices={prestation.prices}
              priceHeader={priceHeaderOf(prestation)}
              isCheques={prestation.type === PrestationType.cheques}
            />
          )}
          <PrestationQuestions prestationId={prestationId} questions={prestation.questions} />
          {isSubsidisedByBilletterie(prestation) && (
            <PrestationSubsidyRules prestation={prestation} />
          )}
          <PrestationJustificatifSetting prestation={prestation} />
        </TabsContent>
      </StickyHeaderPage>
    </Tabs>
  );
}

interface PriceRow {
  _key: string;
  id?: number;
  rank?: number;
  name: string;
  price: Decimal | null;
  attributionCriteria: FaceValueCriteria | null;
}

interface PriceErrors {
  name?: string;
  price?: string;
}

interface PriceEditingContextValue {
  editingId: number | 'new' | null;
  editValues: { name: string; price: Decimal | null };
  editErrors: PriceErrors;
  setEditValues: (updates: Partial<{ name: string; price: Decimal | null }>) => void;
  setEditErrors: (errors: PriceErrors) => void;
  saveEditing: () => void;
  cancelEditing: () => void;
  startEditing: (id: number | 'new', name?: string, price?: Decimal | null) => void;
  isRowEditing: (row: PriceRow) => boolean;
  prestationId: number;
}

const PriceEditingContext = createContext<PriceEditingContextValue | null>(null);

function usePriceEditing() {
  const context = useContext(PriceEditingContext);
  if (!context) throw new Error('usePriceEditing must be used within PriceEditingContext');
  return context;
}

interface PrestationTarifsProps {
  prestationId: number;
  prices: {
    id: number;
    name: string;
    price: Decimal;
    attributionCriteria: FaceValueCriteria | null;
  }[];
  priceHeader: PriceHeader;
  isCheques: boolean;
}

function validatePriceRow(values: { name: string; price: Decimal | null }): PriceErrors | null {
  const errors: PriceErrors = {};
  if (!values.name.trim()) {
    errors.name = 'Requis';
  }
  if (values.price === null) {
    errors.price = 'Requis';
  }
  return Object.keys(errors).length > 0 ? errors : null;
}

function PrestationTarifs({ prestationId, prices, priceHeader, isCheques }: PrestationTarifsProps) {
  const createPrice = useCreatePrestationPrice(prestationId);
  const priceColumns = useMemo(
    () => buildPriceColumns(priceHeader, isCheques),
    [priceHeader, isCheques]
  );
  const tableState = useTableState('prestation-details', { columns: priceColumns });
  const [editingId, setEditingId] = useState<number | 'new' | null>(null);
  const [editValues, setEditValuesState] = useState<{ name: string; price: Decimal | null }>({
    name: '',
    price: null,
  });
  const [editErrors, setEditErrors] = useState<PriceErrors>({});

  const rows: PriceRow[] = useMemo(() => {
    const existingRows: PriceRow[] = prices.map((p, index) => ({
      _key: `existing-${p.id}`,
      id: p.id,
      rank: index + 1,
      name: p.name,
      price: p.price,
      attributionCriteria: p.attributionCriteria,
    }));
    if (editingId === 'new') {
      existingRows.push({
        _key: 'new',
        name: editValues.name,
        price: editValues.price,
        attributionCriteria: null,
      });
    }
    return existingRows;
  }, [prices, editingId, editValues]);

  const startEditing = useCallback(
    (id: number | 'new', name = '', price: Decimal | null = null) => {
      setEditingId(id);
      setEditValuesState({ name, price });
    },
    []
  );

  const cancelEditing = useCallback(() => {
    setEditingId(null);
    setEditValuesState({ name: '', price: null });
    setEditErrors({});
  }, []);

  const saveEditing = useCallback(async () => {
    const errors = validatePriceRow(editValues);
    if (errors) {
      setEditErrors(errors);
      return;
    }
    setEditErrors({});
    if (editingId === 'new') {
      await createPrice.mutate({ name: editValues.name, price: Number(editValues.price) });
      cancelEditing();
    }
  }, [editingId, editValues, createPrice, cancelEditing]);

  const setEditValues = useCallback((updates: Partial<{ name: string; price: Decimal | null }>) => {
    setEditValuesState((prev) => ({ ...prev, ...updates }));
  }, []);

  const isRowEditing = useCallback(
    (row: PriceRow) =>
      (row.id !== undefined && editingId === row.id) ||
      (row.id === undefined && editingId === 'new'),
    [editingId]
  );

  const contextValue = useMemo<PriceEditingContextValue>(
    () => ({
      editingId,
      editValues,
      editErrors,
      setEditValues,
      setEditErrors,
      saveEditing,
      cancelEditing,
      startEditing,
      isRowEditing,
      prestationId,
    }),
    [
      editingId,
      editValues,
      editErrors,
      setEditValues,
      saveEditing,
      cancelEditing,
      startEditing,
      isRowEditing,
      prestationId,
    ]
  );

  return (
    <PriceEditingContext.Provider value={contextValue}>
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium">Tarifs individuels</h3>
          <Button
            variant="color"
            icon={Plus}
            onClick={() => startEditing('new')}
            disabled={editingId !== null}
          >
            Ajouter un tarif
          </Button>
        </div>
        {isCheques && (
          <p className="text-sm text-gray-500">
            À l'inscription, chaque bénéficiaire reçoit la première valeur faciale, dans l'ordre
            ci-dessous, dont tous les critères correspondent. Si une donnée manque ou que rien ne
            correspond, le choix reste manuel. Sans critère sur aucune valeur, tout le monde part
            sur la première. Les dossiers existants gardent leur tarif quand les critères changent.
          </p>
        )}
        {rows.length > 0 ? (
          <Table
            entity={{ singular: 'tarif' }}
            data={rows}
            columns={priceColumns}
            tableState={tableState}
            disableHover
          />
        ) : (
          <div className="text-gray-500 text-sm">Aucun tarif configuré pour cette prestation.</div>
        )}
      </div>
    </PriceEditingContext.Provider>
  );
}

const priceColumnHelper = createColumnHelper<PriceRow>();

function PriceNameCell({ row }: { row: { original: PriceRow } }) {
  const { editValues, editErrors, setEditValues, isRowEditing } = usePriceEditing();

  if (isRowEditing(row.original)) {
    return (
      <div className="flex flex-col gap-1">
        <Input
          value={editValues.name}
          onChange={(e) => setEditValues({ name: e.target.value })}
          className={`h-8 ${editErrors.name ? 'border-red-500' : ''}`}
          placeholder="Nom du tarif"
        />
        {editErrors.name && <span className="text-sm text-red-500">{editErrors.name}</span>}
      </div>
    );
  }
  return row.original.name;
}

function PricePriceCell({ row }: { row: { original: PriceRow } }) {
  const { editValues, editErrors, setEditValues, isRowEditing } = usePriceEditing();

  if (isRowEditing(row.original)) {
    return (
      <div className="flex flex-col gap-1">
        <DecimalInput
          value={editValues.price}
          onChange={(val) => setEditValues({ price: val })}
          className={`h-8 w-32 ${editErrors.price ? 'border-red-500' : ''}`}
          onlyPositive
          placeholder="Saisir un montant"
        />
        {editErrors.price && <span className="text-sm text-red-500">{editErrors.price}</span>}
      </div>
    );
  }
  return row.original.price ? formatDecimalAsPrice(row.original.price) : '-';
}

function PriceCriteriaCell({ row }: { row: { original: PriceRow } }) {
  const { editingId, prestationId } = usePriceEditing();
  const { id, name, attributionCriteria } = row.original;
  if (id === undefined) return '-';

  return (
    <div className="flex items-center justify-between gap-2">
      {attributionCriteria ? (
        describeFaceValueCriteria(attributionCriteria)
      ) : (
        <span className="text-gray-500">Attribution manuelle</span>
      )}
      <ModalButton
        button={(openModal) => (
          <Button
            variant="outline"
            icon={SlidersHorizontal}
            onClick={openModal}
            className="w-8 h-8 shrink-0"
            title="Critères d'attribution"
            disabled={editingId !== null}
          />
        )}
        modal={(open, closeModal) =>
          open && (
            <FaceValueCriteriaModal
              prestationId={prestationId}
              price={{ id, name, attributionCriteria }}
              closeModal={closeModal}
            />
          )
        }
      />
    </div>
  );
}

function PriceActionsCell({ row }: { row: { original: PriceRow } }) {
  const {
    editingId,
    editValues,
    setEditErrors,
    saveEditing,
    cancelEditing,
    startEditing,
    isRowEditing,
    prestationId,
  } = usePriceEditing();
  const priceId = row.original.id;
  const isEditing = isRowEditing(row.original);

  const updatePrice = useUpdatePrestationPrice(prestationId, row.original.id ?? 0);
  const deletePrice = useDeletePrestationPrice(prestationId, row.original.id ?? 0);

  const handleSave = async () => {
    if (row.original.id !== undefined) {
      const errors = validatePriceRow(editValues);
      if (errors) {
        setEditErrors(errors);
        return;
      }
      setEditErrors({});
      await updatePrice.mutate({ name: editValues.name, price: Number(editValues.price) });
      cancelEditing();
    } else {
      saveEditing();
    }
  };

  return (
    <div
      className="flex items-center gap-1"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      {isEditing ? (
        <>
          <Button
            variant="color"
            icon={Check}
            onClick={handleSave}
            className="w-8 h-8"
            title="Valider"
            disabled={updatePrice.isPending}
          />
          <Button
            variant="outline"
            color="red"
            icon={X}
            onClick={cancelEditing}
            className="w-8 h-8"
            title="Annuler"
          />
        </>
      ) : priceId !== undefined ? (
        <Button
          variant="outline"
          icon={Pencil}
          onClick={() => startEditing(priceId, row.original.name, row.original.price)}
          className="w-8 h-8"
          title="Modifier"
          disabled={editingId !== null}
        />
      ) : null}
      {priceId !== undefined && !isEditing && (
        <Button
          variant="outline"
          color="red"
          icon={Trash}
          onClick={() => deletePrice.mutate()}
          className="w-8 h-8"
          title="Supprimer"
          disabled={editingId !== null}
        />
      )}
    </div>
  );
}

type PriceHeader = { label: string; tooltip: string };

const PRICE_HEADERS: Record<PrestationKind, PriceHeader> = {
  sale: {
    label: 'Montant achat',
    tooltip: "Si aucune subvention ne s'applique, le prix de vente est égal au montant d'achat",
  },
  creance: {
    label: 'Montant subventionné',
    tooltip:
      'Montant intégralement pris en charge par le CSE et dû au bénéficiaire (part CE 100 %)',
  },
  adhesion: {
    label: 'Montant demandé',
    tooltip: 'Montant intégralement dû par le salarié au CSE, sans subvention',
  },
};

const SUBSIDISED_ADHESION_PRICE_HEADER: PriceHeader = {
  label: 'Montant demandé',
  tooltip: 'Montant dû au CSE : le salarié en règle ce que la subvention ne couvre pas',
};

const priceHeaderOf = (
  prestation: Parameters<typeof isSubsidisedByBilletterie>[0]
): PriceHeader => {
  const kind = getPrestationKind(prestation);
  return kind === 'adhesion' && isSubsidisedByBilletterie(prestation)
    ? SUBSIDISED_ADHESION_PRICE_HEADER
    : PRICE_HEADERS[kind];
};

const buildPriceColumns = (header: PriceHeader, isCheques: boolean): ColumnDef<PriceRow>[] =>
  [
    ...(isCheques
      ? [
          priceColumnHelper.accessor('rank', {
            header: 'Ordre',
            cell: ({ row }) => row.original.rank ?? '-',
            size: 80,
            meta: { filterType: 'none' },
          }),
        ]
      : []),
    priceColumnHelper.accessor('name', {
      header: 'Intitulé',
      cell: ({ row }) => <PriceNameCell row={row} />,
      size: 300,
      meta: { filterType: 'text' },
    }),
    priceColumnHelper.accessor('price', {
      header: () => (
        <>
          {header.label}
          <Tooltip text={header.tooltip}>
            <CircleQuestionMark className="size-4 shrink-0 text-gray-400" />
          </Tooltip>
        </>
      ),
      cell: ({ row }) => <PricePriceCell row={row} />,
      size: 150,
      meta: { filterType: 'numeric' },
    }),
    ...(isCheques
      ? [
          priceColumnHelper.display({
            id: 'attributionCriteria',
            header: "Critères d'attribution",
            cell: ({ row }) => <PriceCriteriaCell row={row} />,
            size: 420,
          }),
        ]
      : []),
    priceColumnHelper.display({
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => <PriceActionsCell row={row} />,
      size: 100,
    }),
  ] as ColumnDef<PriceRow>[];
